// client/src/dnd/components/pixi_parts/collisionIndex.js
export function buildCollisionIndex({
  imageSize = null,
  wallPolygons = [],
  renderedTokens = [],
  gridConfig = {},
  fallbackTokenRadius = 18,
}) {
  const metreInPixels =
    Number(gridConfig?.metre_in_pixels ?? gridConfig?.meter_in_pixels) || 0;

  const cellSize = pickCellSize({
    gridConfig,
    fallbackTokenRadius,
    metreInPixels,
  });

  const wallSegments = buildWallSegmentsImageSpace(wallPolygons);
  const tokenEntries = buildTokenEntries({
    renderedTokens,
    metreInPixels,
    fallbackTokenRadius,
  });

  const wallGrid = buildSpatialHash({
    items: wallSegments,
    cellSize,
    getBounds: getSegmentBounds,
  });

  const tokenGrid = buildSpatialHash({
    items: tokenEntries,
    cellSize,
    getBounds: getCircleBounds,
  });

  return {
    version: Date.now(),
    imageSize: normalizeImageSize(imageSize),
    cellSize,
    walls: {
      count: wallSegments.length,
      segments: wallSegments,
      grid: wallGrid,
    },
    tokens: {
      count: tokenEntries.length,
      entries: tokenEntries,
      grid: tokenGrid,
    },
  };
}

export function queryCollisionIndex(index, bounds) {
  if (!index || !bounds) {
    return {
      wallSegments: [],
      tokens: [],
    };
  }

  const walls = querySpatialHash(index?.walls?.grid, bounds);
  const tokens = querySpatialHash(index?.tokens?.grid, bounds);

  return {
    wallSegments: walls,
    tokens,
  };
}

export function getSweptCircleBounds(x0, y0, x1, y1, radius = 0) {
  const r = Math.max(0, Number(radius) || 0);
  return {
    minX: Math.min(Number(x0) || 0, Number(x1) || 0) - r,
    minY: Math.min(Number(y0) || 0, Number(y1) || 0) - r,
    maxX: Math.max(Number(x0) || 0, Number(x1) || 0) + r,
    maxY: Math.max(Number(y0) || 0, Number(y1) || 0) + r,
  };
}

function normalizeImageSize(imageSize) {
  if (!Array.isArray(imageSize) || imageSize.length < 2) {
    return { width: 0, height: 0 };
  }

  return {
    width: Math.max(0, Number(imageSize[0]) || 0),
    height: Math.max(0, Number(imageSize[1]) || 0),
  };
}

function pickCellSize({ gridConfig, fallbackTokenRadius, metreInPixels }) {
  const squareLength = Number(gridConfig?.square_length || 0);
  const cellSize = Number(gridConfig?.cell_size || 0);
  const base =
    squareLength > 0
      ? squareLength
      : cellSize > 0
        ? cellSize
        : metreInPixels > 0
          ? metreInPixels * 2
          : fallbackTokenRadius * 4;

  return Math.max(48, Math.round(base || 64));
}

function buildWallSegmentsImageSpace(wallPolygons) {
  const source = Array.isArray(wallPolygons) ? wallPolygons : [];
  const out = [];

  for (let p = 0; p < source.length; p += 1) {
    const poly = Array.isArray(source[p]) ? source[p] : [];
    const pts = poly
      .map((pt) => ({
        x: Number(pt?.x),
        y: Number(pt?.y),
      }))
      .filter((pt) => Number.isFinite(pt.x) && Number.isFinite(pt.y));

    if (pts.length < 2) continue;

    for (let i = 0; i < pts.length - 1; i += 1) {
      const a = pts[i];
      const b = pts[i + 1];
      if (samePoint(a, b)) continue;
      out.push({
        id: `wall:${p}:${i}`,
        a,
        b,
      });
    }

    const first = pts[0];
    const last = pts[pts.length - 1];
    if (!samePoint(first, last)) {
      out.push({
        id: `wall:${p}:close`,
        a: last,
        b: first,
      });
    }
  }

  return out;
}

function buildTokenEntries({
  renderedTokens,
  metreInPixels,
  fallbackTokenRadius,
}) {
  const source = Array.isArray(renderedTokens) ? renderedTokens : [];

  return source
    .map((token, idx) => {
      const x = Number(token?.x);
      const y = Number(token?.y);
      if (!Number.isFinite(x) || !Number.isFinite(y)) return null;

      const rawId = token?.id != null ? String(token.id) : `rendered-${idx}`;
      const tokenType =
        String(token?.type || "player").toLowerCase() === "npc" ? "npc" : "player";

      return {
        id: `character:${tokenType}:${rawId}`,
        source_id: rawId,
        token_type: tokenType,
        x,
        y,
        radius: Math.max(
          1,
          Number(token?.collision_radius) ||
            Number(token?.radius) ||
            Number(token?.size_px) * 0.5 ||
            Number(token?.size) * 0.5 * (metreInPixels > 0 ? metreInPixels : 1) ||
            fallbackTokenRadius
        ),
      };
    })
    .filter(Boolean);
}

function buildSpatialHash({ items, cellSize, getBounds }) {
  const cells = Object.create(null);
  const byId = Object.create(null);

  for (let i = 0; i < items.length; i += 1) {
    const item = items[i];
    if (!item || !item.id) continue;

    byId[item.id] = item;

    const bounds = getBounds(item);
    if (!bounds) continue;

    const minCellX = Math.floor(bounds.minX / cellSize);
    const minCellY = Math.floor(bounds.minY / cellSize);
    const maxCellX = Math.floor(bounds.maxX / cellSize);
    const maxCellY = Math.floor(bounds.maxY / cellSize);

    for (let cy = minCellY; cy <= maxCellY; cy += 1) {
      for (let cx = minCellX; cx <= maxCellX; cx += 1) {
        const key = `${cx}:${cy}`;
        if (!cells[key]) cells[key] = [];
        cells[key].push(item.id);
      }
    }
  }

  return {
    cellSize,
    cells,
    byId,
  };
}

function querySpatialHash(grid, bounds) {
  if (!grid || !bounds) return [];

  const cellSize = Math.max(1, Number(grid.cellSize) || 1);
  const minCellX = Math.floor(bounds.minX / cellSize);
  const minCellY = Math.floor(bounds.minY / cellSize);
  const maxCellX = Math.floor(bounds.maxX / cellSize);
  const maxCellY = Math.floor(bounds.maxY / cellSize);

  const seen = new Set();
  const out = [];

  for (let cy = minCellY; cy <= maxCellY; cy += 1) {
    for (let cx = minCellX; cx <= maxCellX; cx += 1) {
      const key = `${cx}:${cy}`;
      const ids = grid.cells[key];
      if (!Array.isArray(ids) || ids.length === 0) continue;

      for (let i = 0; i < ids.length; i += 1) {
        const id = ids[i];
        if (seen.has(id)) continue;
        seen.add(id);

        const item = grid.byId[id];
        if (item) out.push(item);
      }
    }
  }

  return out;
}

function getSegmentBounds(seg) {
  if (!seg?.a || !seg?.b) return null;
  return {
    minX: Math.min(seg.a.x, seg.b.x),
    minY: Math.min(seg.a.y, seg.b.y),
    maxX: Math.max(seg.a.x, seg.b.x),
    maxY: Math.max(seg.a.y, seg.b.y),
  };
}

function getCircleBounds(circle) {
  if (!circle) return null;
  const r = Math.max(0, Number(circle.radius) || 0);
  return {
    minX: Number(circle.x) - r,
    minY: Number(circle.y) - r,
    maxX: Number(circle.x) + r,
    maxY: Number(circle.y) + r,
  };
}

function samePoint(a, b) {
  if (!a || !b) return false;
  return a.x === b.x && a.y === b.y;
}